import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * SystemLogic Documentation Component
 * 
 * Displays calculation methodologies, formulas, and data pipeline schemas.
 * Rendered via createPortal to document.body at z-[9999] to prevent z-index clipping.
 */
const SystemLogic = ({ onClose, isSuperAdmin = false }) => {
    const [activeTab, setActiveTab] = useState('calls');

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const baseTabs = [
        { id: 'calls', label: 'Calls Logic (HubSpot)', icon: 'call' },
        { id: 'dialers', label: '3CX & Day-1 Dials', icon: 'assessment' },
        { id: 'meetings', label: 'Meetings Logic', icon: 'groups' },
        { id: 'deals', label: 'Deals Logic', icon: 'handshake' },
    ];

    const adminTabs = [
        { id: 'inventory', label: 'Inventory Logic', icon: 'inventory_2' },
        { id: 'alerts', label: 'Alerts System', icon: 'notification_important', isAlert: true },
    ];

    const tabs = isSuperAdmin ? [...baseTabs, ...adminTabs] : baseTabs;

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 font-['Lexend_Deca'] animate-fade-in">
            {/* Viewport Dimming Backdrop */}
            <div 
                className="fixed inset-0 bg-slate-950/70 backdrop-blur-md transition-opacity cursor-pointer"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Modal Dialog Card */}
            <motion.div 
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-5xl h-[85vh] rounded-3xl shadow-2xl flex overflow-hidden z-10 text-slate-800 dark:text-slate-100"
            >
                {/* Left Sidebar */}
                <div className="w-64 bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col flex-shrink-0">
                    <div className="p-6 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-1">
                            <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                                <ObliqueIcon name="calculate" size={16} filled />
                            </div>
                            <span className="font-bold tracking-tight text-sm text-slate-900 dark:text-white">System Logic</span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Calculation Methodologies & Formulas</p>
                    </div>

                    <nav className="flex-1 p-3.5 space-y-1 overflow-y-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                                    tab.isAlert
                                        ? activeTab === tab.id
                                            ? 'bg-red-500 text-white shadow-sm'
                                            : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 border border-red-200 dark:border-red-900/30'
                                        : activeTab === tab.id
                                            ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-500/30'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-850'
                                }`}
                            >
                                <div className="flex items-center gap-2.5">
                                    <ObliqueIcon name={tab.icon} size={16} />
                                    <span>{tab.label}</span>
                                </div>
                                {activeTab === tab.id && <ObliqueIcon name="chevron_right" size={14} />}
                            </button>
                        ))}
                    </nav>

                    <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                        <button
                            onClick={onClose}
                            className="w-full py-2 px-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors text-xs cursor-pointer shadow-sm"
                        >
                            Close Documentation
                        </button>
                    </div>
                </div>

                {/* Right Content Area */}
                <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-900 p-8 md:p-10">
                    <div className="max-w-3xl mx-auto space-y-8">
                        {/* Header */}
                        <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    {tabs.find(t => t.id === activeTab)?.label}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Detailed breakdown of how metrics are calculated, filtered, and aggregated.</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                                title="Close (Esc)"
                            >
                                <ObliqueIcon name="close" size={18} />
                            </button>
                        </div>

                        {/* 3CX & DAY-1 DIALS LOGIC */}
                        {activeTab === 'dialers' && (
                            <div className="space-y-6 animate-fade-in">
                                <section className="space-y-3">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">1</span>
                                        3CX Column Headers & Schema
                                    </h4>
                                    <div className="pl-7 space-y-2 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                        <p>The 3CX Day-1 Ingestion pipeline processes exports containing the following standard columns:</p>
                                        <div className="p-3.5 bg-slate-900 text-slate-200 rounded-xl font-mono text-[11px] leading-relaxed border border-slate-800">
                                            Call Time · Call ID · From · To · Direction · Status · Ringing · Talking · Cost · Call Activity Details · Sentiment · Summary · Transcription
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">2</span>
                                        Summary / Totals Row Exclusion
                                    </h4>
                                    <div className="pl-7 space-y-2 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300">
                                            <strong>Automatic Filtering:</strong> 3CX reports frequently include a trailing "Total:" summary row with empty Call IDs and aggregated talk times (e.g. 2h 54m). The ingestion parser automatically identifies and excludes this row to eliminate phantom <code>(Unknown / Blank)</code> rows and duplicate counts.
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-3">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">3</span>
                                        Mathematical Formulas & Aggregations
                                    </h4>
                                    <div className="pl-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Total Calls</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2"><code>COUNTA</code> of the <strong>From</strong> column per sales rep (excluding summary row).</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">Total Calls = Count(Valid Rows)</code>
                                        </div>

                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Answered vs Unanswered</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Evaluated via <code>Status == 'Answered'</code> or <code>Talking &gt; 00:00:00</code>.</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">Unanswered = Total - Answered</code>
                                        </div>

                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Unique Dials</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Distinct count of normalized destination phone numbers per agent.</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">Set(Normalized_To_Numbers)</code>
                                        </div>

                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Attempts / Number</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Average dial frequency per distinct prospect phone number.</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">Total Calls / Unique Dials</code>
                                        </div>

                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Connect Rate (%)</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Percentage of dials where conversation or answer occurred.</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">(Answered / Total Calls) * 100</code>
                                        </div>

                                        <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-[11px] font-bold text-slate-900 dark:text-white uppercase mb-1">Average Handling Time (AHT)</div>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Average conversation duration per answered call.</p>
                                            <code className="text-[10px] bg-slate-900 text-emerald-400 px-2 py-1 rounded">Total Talk Time / Answered Calls</code>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* CALLS LOGIC */}
                        {activeTab === 'calls' && (
                            <div className="space-y-6 animate-fade-in">
                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">1</span>
                                        Slot Analysis
                                    </h4>
                                    <div className="pl-7 text-xs text-slate-600 dark:text-slate-300 space-y-2">
                                        <p>Calls are bucketed into three time slots based on EST time zones to track shift performance:</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                                            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800">
                                                <div className="text-[10px] font-bold text-slate-400 uppercase">First 3 Hours</div>
                                                <div className="text-xs font-bold text-slate-900 dark:text-white mt-1">Before 12:00 PM EST</div>
                                            </div>
                                            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800">
                                                <div className="text-[10px] font-bold text-slate-400 uppercase">Mid 3 Hours</div>
                                                <div className="text-xs font-bold text-slate-900 dark:text-white mt-1">12:00 PM – 03:00 PM EST</div>
                                            </div>
                                            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800">
                                                <div className="text-[10px] font-bold text-slate-400 uppercase">Last 3 Hours</div>
                                                <div className="text-xs font-bold text-slate-900 dark:text-white mt-1">After 03:00 PM EST</div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xs font-bold">2</span>
                                        Unique Dials Calculation
                                    </h4>
                                    <div className="pl-7 space-y-3">
                                        <p className="text-xs text-slate-600 dark:text-slate-300">We identify unique reached clients using a composite key to prevent duplicate counting:</p>
                                        <div className="p-3.5 bg-slate-900 text-slate-200 rounded-xl font-mono text-xs shadow-sm border border-slate-800">
                                            Key = AgentEmail + Date + ContactID + CompanyID
                                        </div>
                                        <p className="text-xs text-slate-500 italic">
                                            Note: Multiple attempts to the same contact on the same day are counted as <strong>one unique dial</strong> contextually.
                                        </p>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* MEETINGS LOGIC */}
                        {activeTab === 'meetings' && (
                            <div className="space-y-6 animate-fade-in">
                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">2nd Voice Rules</h4>
                                    <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 p-4 rounded-2xl">
                                        <ul className="text-amber-800 dark:text-amber-300 text-xs space-y-2">
                                            <li>• <strong>Organizer</strong> = Meeting owner (from organizer_email) → gets full credit</li>
                                            <li>• <strong>2nd Voice</strong> = Another Sales who joined AFTER organizer</li>
                                            <li>• <strong>Sales-to-Sales ONLY</strong> — PM joining Sales meeting ≠ 2nd voice</li>
                                            <li>• <strong>PM-to-PM</strong> — Same logic applies within PM team</li>
                                        </ul>
                                    </div>
                                </section>

                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">BOT/AI Exclusion</h4>
                                    <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 p-4 rounded-2xl">
                                        <p className="text-red-800 dark:text-red-300 text-xs mb-3"><strong>Rule:</strong> Exclude all BOTs from duration calculations.</p>
                                        <code className="block bg-slate-900 text-slate-200 p-3 rounded-xl text-xs font-mono border border-slate-800">
                                            fireflies.ai, otter.ai, gong, fathom, tl;dv, avoma, chorus, grain, meetgeek, krisp
                                        </code>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* DEALS LOGIC */}
                        {activeTab === 'deals' && (
                            <div className="space-y-6 animate-fade-in">
                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Deal Attributes</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-indigo-600 dark:text-indigo-400 font-bold text-xs uppercase mb-2">Revenue Model</div>
                                            <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5">
                                                <li>• <strong>One-Time:</strong> Single transactional payments.</li>
                                                <li>• <strong>Recurring:</strong> Subscription-based models (MRR).</li>
                                            </ul>
                                        </div>
                                        <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50 dark:bg-slate-800/40">
                                            <div className="text-indigo-600 dark:text-indigo-400 font-bold text-xs uppercase mb-2">Performance Metrics</div>
                                            <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5">
                                                <li>• <strong>Total Sales:</strong> Sum of 'Amount' (Closed Won).</li>
                                                <li>• <strong>In-Bank:</strong> Sum of 'Amount Received'.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* INVENTORY LOGIC */}
                        {activeTab === 'inventory' && isSuperAdmin && (
                            <div className="space-y-6 animate-fade-in">
                                <section className="space-y-4">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">PM Inventory Generation</h4>
                                    <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/30 p-4 rounded-2xl">
                                        <p className="text-purple-800 dark:text-purple-300 text-xs mb-3"><strong>Logic:</strong> Sales recurring deal in Month X → PM Inventory for Month X+1</p>
                                        <code className="block bg-slate-900 text-slate-200 p-3 rounded-xl text-xs font-mono border border-slate-800">
                                            Dec 2025 Recurring Sale → Jan 2026 PM Inventory
                                        </code>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* ALERTS SYSTEM */}
                        {activeTab === 'alerts' && isSuperAdmin && (
                            <div className="space-y-6 animate-fade-in">
                                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 p-4 rounded-2xl">
                                    <p className="text-red-700 dark:text-red-300 text-xs font-semibold flex items-center gap-2">
                                        <ObliqueIcon name="notification_important" size={16} />
                                        Automated triggers for quality control and sales-to-PM inventory protection
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>,
        document.body
    );
};

export default SystemLogic;
